import { isExtractionTransferReady } from './recognized-stage'
import type { ExtractionField, ExtractionStage } from '../../../api/listeners/document-extraction'
import { isExtractedTextualDate, normalizeExtractedBirthDate } from './recognized-birthdate'
import { normalizeExtractedName } from './recognized-names'
import { normalizePassportIdentifier, normalizePassportIssuer } from './recognized-passport'

export type ListenerEducation = { diplom: string; educationLevelID: number | string; educationLevel: string; maidenName: string }
export type RecognizedEducation = Partial<ListenerEducation>
export type EducationPacket = { diplom?: string; higherEducation?: boolean; maidenName?: string }

type PartResult = NonNullable<ExtractionStage['parts'][number]['result']>
type Document = PartResult['documents'][number]
type Source = { result: PartResult; document: Document }
const unconfirmedLinks = new Set(['person_link_unconfirmed', 'document_link_unconfirmed'])
const textFields = ['documentSeries', 'documentNumber', 'institutionName', 'registrationNumber', 'issueDate', 'qualificationAsPrinted', 'degreeAsPrinted', 'specialtyAsPrinted'] as const
const sameText = (left?: string | null, right?: string | null) => left?.toLocaleLowerCase('ru') === right?.toLocaleLowerCase('ru')

export const normalizeEducationText = (value: string): string | null => {
    const text = value.normalize('NFC').trim().replace(/\s+/gu, ' ')
    const hasControl = [...text].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
    return text && /[\p{L}\p{N}]/u.test(text) && !hasControl && !/[<>]/u.test(text) ? text : null
}

export const normalizeEducationIdentifier = (value: string) => normalizePassportIdentifier(value.trim().replace(/^№\s*/u, ''))
const quotedText = (value: string) => {
    const text = normalizeEducationText(value)
    if (!text) return null
    const pairs = [['"', '"'], ['«', '»'], ['“', '”'], ["'", "'"]]
    const pair = pairs.find(([left, right]) => text.startsWith(left) && text.endsWith(right))
    return pair ? normalizeEducationText(text.slice(1, -1)) : text
}

const relevantConflicts = ({ result, document }: Source, name: string) => result.conflicts.filter(item => !unconfirmedLinks.has(item.code)
    && item.fields.some(reference => reference.documentId === document.documentId && reference.field === name))
const relevantUnresolved = ({ result, document }: Source, name: string) => result.unresolved.filter(item => !unconfirmedLinks.has(item.code)
    && item.documentId === document.documentId && item.field === name)

const absentQualification = (field: ExtractionField | undefined) => !field
    || (['extracted', 'not_present', 'not_applicable'].includes(field.status)
        && !field.rawValue?.trim() && !field.normalizedValue?.trim() && !field.candidates.length
        && !['failed', 'invalid', 'conflict'].includes(field.validation.format)
        && !['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument))

const fieldValue = (field: ExtractionField | undefined, normalize: (value: string) => string | null, allowNameMismatch = false) => {
    if (!field || ['not_present', 'not_applicable'].includes(field.status)) return { value: null, needsReview: false }
    const rawText = field.rawValue?.trim() || '', normalizedText = field.normalizedValue?.trim() || ''
    const raw = rawText ? normalize(rawText) : null, normalized = normalizedText ? normalize(normalizedText) : null
    const value = normalized || raw
    if (!value || !field.evidence.length || field.candidates.length
        || (field.status !== 'extracted' && !(allowNameMismatch && field.status === 'conflict'))
        || ['failed', 'invalid', 'conflict'].includes(field.validation.format)
        || (['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument) && !(allowNameMismatch && field.validation.crossDocument === 'conflict'))
        || (rawText && !raw) || (normalizedText && !normalized) || (raw && normalized && !sameText(raw, normalized))) return { value: null, needsReview: true }
    return { value, needsReview: false }
}

const sourceNames = (source: Source) => {
    const read = (name: string) => {
        const conflicts = relevantConflicts(source, name)
        if (relevantUnresolved(source, name).length || conflicts.some(item => item.code !== 'name_mismatch')) return null
        return fieldValue(source.document.fields.find(field => field.name === name), normalizeExtractedName,
            conflicts.length > 0 && conflicts.every(item => item.code === 'name_mismatch')).value
    }
    const lastName = read('lastName'), firstName = read('firstName'), middleName = read('middleName')
    return lastName && firstName && middleName ? { lastName, firstName, middleName } : null
}

export const recognizedEducationPackets = (stage: ExtractionStage | null): { packets: EducationPacket[]; needsReview: boolean } => {
    let packets: EducationPacket[] = [], needsReview = false
    if (!stage || !isExtractionTransferReady(stage)) return { packets, needsReview }
    const sources: Source[] = stage.parts.filter(part => part.status === 'succeeded' && part.result)
        .flatMap(part => part.result!.documents.map(document => ({ result: part.result!, document })))
    const passportNames = sources.filter(source => source.document.type === 'passport_main').map(sourceNames)
    const passport = passportNames.length && passportNames.every(names => names && JSON.stringify(names) === JSON.stringify(passportNames[0])) ? passportNames[0] : null
    const readings: Record<string, string | null>[] = []
    for (const source of sources.filter(item => item.document.type === 'education')) {
        let documentNeedsReview = false
        const read = (name: string, normalize: (value: string) => string | null) => {
            const field = source.document.fields.find(item => item.name === name)
            const conflict = relevantConflicts(source, name).length || relevantUnresolved(source, name).length
            // The model used to leave a full written month unnormalized. Resolve
            // that exact representation, without guessing a numeric/unclear date.
            if (!conflict && name === 'issueDate' && field?.status === 'ambiguous' && field.normalizedValue === null
                && field.evidence.length > 0 && !field.candidates.length && field.confidence >= 0.95
                && ['not_checked', 'failed'].includes(field.validation.format)
                && ['not_checked', 'passed'].includes(field.validation.crossDocument)
                && field.rawValue && isExtractedTextualDate(field.rawValue)) {
                const date = normalizeExtractedBirthDate(field.rawValue)
                if (date) return date
            }
            const value = fieldValue(field, normalize)
            if (value.needsReview || conflict) documentNeedsReview = true
            return conflict ? null : value.value
        }
        const series = read('documentSeries', normalizeEducationIdentifier), number = read('documentNumber', normalizeEducationIdentifier)
        const institution = read('institutionName', normalizePassportIssuer), registration = read('registrationNumber', normalizeEducationIdentifier)
        const date = read('issueDate', normalizeExtractedBirthDate), specialty = read('specialtyAsPrinted', quotedText)
        const fallback = absentQualification(source.document.fields.find(field => field.name === 'qualificationAsPrinted'))
            && !relevantConflicts(source, 'qualificationAsPrinted').length
            && !relevantUnresolved(source, 'qualificationAsPrinted').some(item => item.code !== 'missing_field')
        // A missing qualification may use the printed degree. An unreadable or
        // contradictory primary field still needs review instead of a fallback.
        const qualification = fallback ? null : read('qualificationAsPrinted', quotedText)
        const degree = fallback ? read('degreeAsPrinted', quotedText) : null
        const kind = read('documentKind', normalizeEducationText)
        if (documentNeedsReview) needsReview = true
        readings.push({ documentSeries: series, documentNumber: number, institutionName: institution, registrationNumber: registration,
            issueDate: date, qualificationAsPrinted: qualification, degreeAsPrinted: degree, specialtyAsPrinted: specialty })
        const packet: EducationPacket = {}
        const credential = qualification || degree
        if (series && number && institution && registration && date && credential && specialty) {
            packet.diplom = `${fallback ? '№ ' : ''}${series} ${number}, выдан ${institution}, рег.номер ${registration}, дата выдачи ${date} г., ${fallback ? 'степень' : 'квалификация'} "${credential}" по направлению "${specialty}"`
        }
        if (kind && /(?:^|[^\p{L}\p{N}_])диплом(?:$|[^\p{L}\p{N}_])/iu.test(kind)) packet.higherEducation = true
        const names = sourceNames(source)
        if (passport && names && names.firstName === passport.firstName && names.middleName === passport.middleName && names.lastName !== passport.lastName) packet.maidenName = names.lastName
        if (!Object.keys(packet).length && textFields.some(name => source.document.fields.find(field => field.name === name)?.rawValue?.trim())) needsReview = true
        if (Object.keys(packet).length && !packets.some(item => sameText(item.diplom, packet.diplom) && item.higherEducation === packet.higherEducation && item.maidenName === packet.maidenName)) packets.push(packet)
    }
    // A partial second diploma must not hide different readable requisites.
    for (const name of textFields) {
        const values = readings.map(reading => reading[name]?.toLocaleLowerCase('ru')).filter(Boolean)
        if (new Set(values).size > 1) needsReview = true
    }
    const complete = packets.find(candidate => packets.every(packet => (!packet.diplom || sameText(packet.diplom, candidate.diplom))
        && (!packet.higherEducation || candidate.higherEducation) && (!packet.maidenName || candidate.maidenName === packet.maidenName)))
    if (complete) packets = [complete]
    return { packets, needsReview }
}
